<template>
  <div class="w-full flex items-end">
    <div class="md:hidden flex justify-between items-center">
      <button
        @click="isMobileMenuOpen = true"
        class="px-4 py-2 min-h-[46px] flex items-center gap-2 ms-auto bg-[#386f99] text-white rounded-xl text-sm font-semibold active:scale-95 transition-all shadow-sm"
      >
        <svg
          class="text-lg block w-[16px]"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
          <g
            id="SVGRepo_tracerCarrier"
            stroke-linecap="round"
            stroke-linejoin="round"
          ></g>
          <g id="SVGRepo_iconCarrier">
            <path
              d="M19 3H5C3.58579 3 2.87868 3 2.43934 3.4122C2 3.8244 2 4.48782 2 5.81466V6.50448C2 7.54232 2 8.06124 2.2596 8.49142C2.5192 8.9216 2.99347 9.18858 3.94202 9.72255L6.85504 11.3624C7.49146 11.7206 7.80967 11.8998 8.03751 12.0976C8.51199 12.5095 8.80408 12.9935 8.93644 13.5872C9 13.8722 9 14.2058 9 14.8729L9 17.5424C9 18.452 9 18.9067 9.25192 19.2613C9.50385 19.6158 9.95128 19.7907 10.8462 20.1406C12.7248 20.875 13.6641 21.2422 14.3321 20.8244C15 20.4066 15 19.4519 15 17.5424V14.8729C15 14.2058 15 13.8722 15.0636 13.5872C15.1959 12.9935 15.488 12.5095 15.9625 12.0976C16.1903 11.8998 16.5085 11.7206 17.145 11.3624L20.058 9.72255C21.0065 9.18858 21.4808 8.9216 21.7404 8.49142C22 8.06124 22 7.54232 22 6.50448V5.81466C22 4.48782 22 3.8244 21.5607 3.4122C21.1213 3 20.4142 3 19 3Z"
              fill="#ffffff"
            ></path>
          </g></svg
        >Filter
      </button>
    </div>

    <transition name="fade">
      <div
        v-if="isMobileMenuOpen"
        class="fixed inset-0 z-[9499] bg-slate-900/40 backdrop-blur-[2px] md:hidden"
        @click="isMobileMenuOpen = false"
      ></div>
    </transition>

    <div
      class="w-full fixed left-0 right-0 bottom-0 z-[9500] bg-white rounded-t-3xl shadow-2xl transform transition-transform duration-300 ease-out md:relative md:translate-y-0 md:rounded-none md:shadow-none md:bg-transparent md:z-auto"
      :class="[
        isMobileMenuOpen
          ? 'translate-y-0'
          : 'translate-y-full md:translate-y-0',
        'max-h-[80dvh] flex flex-col overflow-hidden',
      ]"
    >
      <div
        class="overflow-y-auto px-3 md:px-2 md:!p-4 pt-0 md:p-0 md:overflow-visible md:!rounded-2xl md:shadow-sm md:border md:border-1"
      >
        <div
          class="flex items-center justify-between mb-4 md:hidden sticky top-0 bg-white py-2.5 z-10"
        >
          <h2 class="text-lg font-extrabold text-slate-800">Filters</h2>
          <button
            @click="isMobileMenuOpen = false"
            class="w-8 h-8 flex items-center justify-center bg-slate-50 text-slate-400 rounded-full"
          >
            <i class="fa-solid fa-xmark text-sm"></i>
          </button>
        </div>

        <div
          class="grid grid-cols-2 md:flex md:flex-row md:items-end gap-x-3 gap-y-3"
        >
          <div
            v-for="(option, index) in options"
            :key="index"
            class="flex flex-col gap-1"
            :class="
              option.type === 'text'
                ? 'col-span-2 md:flex-1'
                : 'col-span-1 md:flex-1'
            "
          >
            <label
              class="text-black text-[9px] font-semibold uppercase tracking-wider ml-0.5"
            >
              {{ option.label }}
            </label>

            <div
              v-if="option.type === 'text' || option.type === 'number'"
              class="relative"
              :class="option.disabled ? 'disabled-input' : ''"
            >
              <input
                :type="option.type"
                :disabled="option.disabled"
                :placeholder="option.placeholder"
                @keyup.enter="handleSearch"
                @input="removeSpaces($event, option.property)"
                v-model="$parent[option.property]"
                class="w-full h-9 px-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 text-xs focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed disabled:border-slate-200 disabled:opacity-80"
              />
            </div>

            <div v-if="option.isDate || option.isDateTime" class="relative">
              <flat-pickr
                v-model="$parent[option.property]"
                :config="getFlatpickrConfig(option)"
                class="w-full h-9 pl-3 pr-5 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 text-xs outline-none"
                :placeholder="option.placeholder"
              />
              <i
                class="fa-solid fa-calendar absolute -translate-y-1/2 top-[50%] right-2.5 text-slate-300 text-[10px]"
              ></i>
            </div>

            <div v-else-if="option.type === 'select'" class="relative">
              <select
                v-model="$parent[option.property]"
                class="w-full h-9 pl-3 pr-7 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 text-xs appearance-none outline-none"
              >
                <option disabled value="">{{ option.placeholder }}</option>
                <option
                  v-for="(val, idx) in option.options"
                  :key="idx"
                  :value="val"
                >
                  {{
                    option?.customLabelForOptions &&
                    option?.customLabelForOptions[idx]
                      ? option?.customLabelForOptions[idx]
                      : val
                  }}
                </option>
              </select>
              <i
                class="fa-solid fa-chevron-down absolute right-2.5 top-3 text-slate-300 text-[9px]"
              ></i>
            </div>
          </div>

          <div
            v-if="showActionButton"
            class="col-span-2 flex items-center gap-2 mt-0 py-3 md:!py-0 border-t border-slate-50 md:border-none md:pt-0 md:mt-0 sticky bottom-0 bg-white"
          >
            <button
              @click="handleSearch"
              class="flex-1  h-10 w-10 bg-[#386f99] text-white text-xs font-bold rounded-full transition-all flex items-center justify-center gap-2 active:scale-95"
            >
              <i class="fa-solid fa-magnifying-glass text-[10px]"></i>
            </button>

            <button
              @click="resetFilter"
              class=" h-10 w-10 bg-slate-100 text-slate-500 rounded-full transition-all flex items-center justify-center"
            >
              <i class="fa-solid fa-arrow-rotate-left text-xs"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
    <slot></slot>
  </div>
</template>

<script>
import FlatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";

export default {
  name: "FilterComponent",
  props: {
    options: { type: Array, required: true },
    showActionButton: { type: Boolean, default: false },
    search: Function,
    resetFilter: Function,
    resetPagination: Function,
  },
  components: { FlatPickr },
  data() {
    return {
      isMobileMenuOpen: false,
    };
  },
  methods: {
    getFlatpickrConfig(option) {
      return {
        enableTime: !!option.isDateTime,
        time_24hr: true,
        dateFormat: option.isDateTime ? "Y-m-d H:i" : "Y-m-d",
        maxDate:
          option.property === "start_date" ? this.$parent.startDateMax : null,
        minDate:
          option.property === "end_date" ? this.$parent.endDateMin : null,
        disableMobile: "true",
      };
    },
    handleSearch() {
      this.isMobileMenuOpen = false;
      if (this.resetPagination) this.resetPagination();
      if (this.search) this.search();
    },
    removeSpaces(event, property) {
      const val = event.target.value.replace(/\s+/g, "");
      this.$parent[property] = val;
    },
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@media (max-width: 767px) {
  .grid-cols-2 {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
.disabled-input{
  cursor: not-allowed!important;
}
</style>
